import { supabase } from '../lib/supabase';

// ── Strava Client ID ──────────────────────────────────────────────────────────
// Set your Strava Client ID here directly so it works without .env changes.
// Get it from: https://www.strava.com/settings/api
const HARDCODED_STRAVA_CLIENT_ID = ''; // ← paste your numeric Client ID here, e.g. '12345'

const _envClientId = import.meta.env?.VITE_STRAVA_CLIENT_ID;
const STRAVA_CLIENT_ID =
  HARDCODED_STRAVA_CLIENT_ID ||
  (_envClientId !== 'your-strava-client-id-here' ? _envClientId : '');

const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;

/**
 * Returns the OAuth redirect URI. Computed lazily to ensure window.location is
 * available and correct in all browsers (including Safari).
 */
function getRedirectUri() {
  return `${window.location?.origin}/strava-callback`;
}

/**
 * Initiates Strava OAuth flow by redirecting to Strava authorization page.
 * Stores the current user ID in localStorage so the callback can use it.
 * NOTE: sessionStorage is intentionally NOT used here because Safari's
 * Intelligent Tracking Prevention (ITP) clears sessionStorage during
 * cross-origin redirects (e.g. app → strava.com → app), causing the
 * userId to be lost before the callback page can read it.
 */
export function initiateStravaOAuth(userId) {
  const REDIRECT_URI = getRedirectUri();
  console.group('[Strava OAuth] initiateStravaOAuth');
  console.log('User ID:', userId);
  console.log('STRAVA_CLIENT_ID resolved:', STRAVA_CLIENT_ID ? `"${STRAVA_CLIENT_ID}"` : '(empty — not configured)');
  console.log('REDIRECT_URI:', REDIRECT_URI);
  console.log('SUPABASE_URL configured:', !!SUPABASE_URL);
  console.log('VITE_STRAVA_CLIENT_ID env raw:', _envClientId);
  console.log('HARDCODED_STRAVA_CLIENT_ID:', HARDCODED_STRAVA_CLIENT_ID || '(empty)');

  if (!STRAVA_CLIENT_ID) {
    const err = new Error(
      'Strava Client ID is not configured. Set HARDCODED_STRAVA_CLIENT_ID in stravaService.js or add VITE_STRAVA_CLIENT_ID to the .env file.'
    );
    console.error('[Strava OAuth] BLOCKED — Client ID missing:', err?.message);
    console.groupEnd();
    throw err;
  }

  if (!userId) {
    const err = new Error('User must be logged in to connect Strava.');
    console.error('[Strava OAuth] BLOCKED — No userId:', err?.message);
    console.groupEnd();
    throw err;
  }

  // Use localStorage instead of sessionStorage — Safari ITP clears sessionStorage
  // during cross-origin OAuth redirects, causing the userId to be lost.
  try {
    localStorage.setItem('strava_oauth_user_id', userId);
    console.log('Saved strava_oauth_user_id to localStorage:', userId);
  } catch (storageErr) {
    console.warn('[Strava OAuth] localStorage write failed, falling back to sessionStorage:', storageErr);
    sessionStorage.setItem('strava_oauth_user_id', userId);
  }

  const params = new URLSearchParams({
    client_id: STRAVA_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    approval_prompt: 'auto',
    scope: 'read,activity:read_all',
  });

  const stravaAuthUrl = `https://www.strava.com/oauth/authorize?${params?.toString()}`;
  console.log('Redirecting to Strava auth URL:', stravaAuthUrl);
  console.groupEnd();

  window.location.href = stravaAuthUrl;
}

/**
 * Exchanges the authorization code for tokens via the Supabase Edge Function.
 * Called from the StravaCallback page after Strava redirects back.
 */
export async function exchangeStravaCode(code, userId) {
  const REDIRECT_URI = getRedirectUri();
  console.group('[Strava OAuth] exchangeStravaCode');
  console.log('Code received:', code ? `${code?.substring(0, 8)}…` : '(none)');
  console.log('User ID:', userId);
  console.log('SUPABASE_URL:', SUPABASE_URL || '(not configured)');
  console.log('REDIRECT_URI:', REDIRECT_URI);

  if (!SUPABASE_URL) {
    const err = new Error('Supabase URL is not configured.');
    console.error('[Strava OAuth] BLOCKED — Supabase URL missing');
    console.groupEnd();
    throw err;
  }

  const edgeFunctionUrl = `${SUPABASE_URL}/functions/v1/fitness-oauth-exchange`;
  console.log('Edge Function URL:', edgeFunctionUrl);

  let response;
  try {
    console.log('Sending POST to Edge Function…');
    response = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': import.meta.env?.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        provider: 'strava',
        code,
        redirectUri: REDIRECT_URI,
        userId,
      }),
    });
    console.log('Edge Function HTTP status:', response?.status, response?.statusText);
  } catch (networkErr) {
    console.error('[Strava OAuth] Network error calling Edge Function:', networkErr);
    console.groupEnd();
    throw new Error(`Network error: Could not reach Supabase Edge Function. ${networkErr?.message}`);
  }

  let data;
  try {
    data = await response?.json();
    console.log('Edge Function response body:', data);
  } catch (parseErr) {
    console.error('[Strava OAuth] Failed to parse Edge Function response as JSON:', parseErr);
    console.groupEnd();
    throw new Error('Invalid response from server (not JSON). Check Edge Function logs.');
  }

  if (!response?.ok) {
    // Extract error — handle both { error: string } and raw Strava JSON { message, errors }
    let errMsg = 'Failed to exchange Strava authorization code';
    if (data?.error) {
      errMsg = data?.error;
    } else if (data?.errors?.length > 0) {
      // Raw Strava error leaked through — parse it
      const e = data?.errors?.[0];
      errMsg = `Strava rejected the request: field="${e?.field}" is ${e?.code}. Check your Strava app's Authorization Callback Domain.`;
    } else if (data?.message) {
      errMsg = data?.message;
    }
    console.error('[Strava OAuth] Token exchange failed. Status:', response?.status, '| Error:', errMsg);

    // Provide more actionable error messages
    if (errMsg?.includes('credentials not configured')) {
      const err = new Error(
        'Strava credentials are not configured in Supabase Edge Function secrets. Please add STRAVA_CLIENT_ID and STRAVA_CLIENT_SECRET in your Supabase project → Edge Functions → Secrets.'
      );
      console.error('[Strava OAuth] Missing secrets:', err?.message);
      console.groupEnd();
      throw err;
    }
    if (errMsg?.includes('Token exchange failed') || errMsg?.includes('redirect_uri')) {
      const err = new Error(
        `Strava token exchange failed. Verify your Strava app's Authorization Callback Domain is set to: ${window.location?.hostname}`
      );
      console.error('[Strava OAuth] Callback domain mismatch likely:', err?.message);
      console.groupEnd();
      throw err;
    }
    console.groupEnd();
    throw new Error(errMsg);
  }

  console.log('[Strava OAuth] Token exchange SUCCESS. Athlete data received:', !!data?.athlete);
  console.groupEnd();
  return data;
}

/**
 * Fetches the current Strava connection for a user from fitness_connections.
 */
export async function getStravaConnection(userId) {
  const { data, error } = await supabase
    ?.from('fitness_connections')
    ?.select('*')
    ?.eq('user_id', userId)
    ?.eq('provider', 'strava')
    ?.maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Disconnects Strava by deleting the fitness_connections record.
 */
export async function disconnectStrava(userId) {
  const { error } = await supabase
    ?.from('fitness_connections')
    ?.delete()
    ?.eq('user_id', userId)
    ?.eq('provider', 'strava');

  if (error) throw error;
}

/**
 * Fetches fitness activities from the fitness_activities table with optional filters.
 */
export async function getFitnessActivities(userId, filters = {}) {
  let query = supabase
    ?.from('fitness_activities')
    ?.select('*')
    ?.eq('user_id', userId)
    ?.order('start_time', { ascending: false });

  if (filters?.provider) {
    query = query?.eq('provider', filters?.provider);
  }

  if (filters?.activityType) {
    query = query?.ilike('activity_type', `%${filters?.activityType}%`);
  }

  if (filters?.startDate) {
    query = query?.gte('start_time', filters?.startDate);
  }

  if (filters?.endDate) {
    const end = new Date(filters.endDate);
    end?.setDate(end?.getDate() + 1);
    query = query?.lt('start_time', end?.toISOString()?.split('T')?.[0]);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Maps a Strava activity_type string to a workout_type used in workout_logs.
 */
function mapStravaTypeToWorkoutType(activityType = '') {
  const map = {
    run: 'running',
    virtualrun: 'running',
    ride: 'cycling',
    virtualride: 'cycling',
    swim: 'swimming',
    walk: 'walking',
    hike: 'hiking',
    weighttraining: 'strength',
    workout: 'strength',
    yoga: 'yoga',
    crossfit: 'hiit',
    elliptical: 'cardio',
    stairstepper: 'cardio',
    rowing: 'cardio',
    iceskate: 'cardio',
    alpineski: 'cardio',
    snowboard: 'cardio',
    soccer: 'sports',
    tennis: 'sports',
    basketball: 'sports',
    golf: 'sports',
  };
  const key = activityType?.toLowerCase()?.replace(/\s+/g, '');
  return map?.[key] || activityType?.toLowerCase() || 'general';
}

/**
 * Imports all fitness_activities (Strava) for a user into workout_logs so they
 * appear directly in Activity Logs without manual entry.
 *
 * Uses the external_activity_id column (added via migration) to prevent duplicates.
 * Safe to call multiple times — already-imported activities are skipped via upsert.
 *
 * @param {string} userId
 * @returns {{ imported: number, skipped: number }}
 */
export async function importStravaActivitiesToLogs(userId) {
  if (!userId) return { imported: 0, skipped: 0 };

  // 1. Fetch all fitness_activities for this user
  const { data: fitnessActivities, error: fetchError } = await supabase
    ?.from('fitness_activities')
    ?.select('*')
    ?.eq('user_id', userId)
    ?.order('start_time', { ascending: false });

  if (fetchError) throw fetchError;
  if (!fitnessActivities?.length) return { imported: 0, skipped: 0 };

  // 2. Fetch existing workout_logs that came from Strava (have external_activity_id set)
  const { data: existingLogs, error: existingError } = await supabase
    ?.from('workout_logs')
    ?.select('external_activity_id')
    ?.eq('user_id', userId)
    ?.not('external_activity_id', 'is', null);

  if (existingError) throw existingError;

  const importedIds = new Set((existingLogs || [])?.map(l => l?.external_activity_id));

  // 3. Build inserts for activities not yet in workout_logs
  const toInsert = fitnessActivities
    ?.filter(fa => !importedIds?.has(fa?.id))
    ?.map(fa => {
      const durationMinutes = fa?.duration
        ? Math.round(fa?.duration / 60)
        : (fa?.duration_seconds ? Math.round(fa?.duration_seconds / 60) : 0);

      const activityDate = fa?.start_time
        ? new Date(fa.start_time)?.toISOString()?.split('T')?.[0]
        : new Date()?.toISOString()?.split('T')?.[0];

      const activityName = fa?.raw_data?.name || fa?.activity_type || 'Strava Activity';

      return {
        user_id: userId,
        workout_type: mapStravaTypeToWorkoutType(fa?.activity_type),
        exercise_name: activityName,
        completed_sets: 0,
        duration_minutes: durationMinutes,
        calories_burned: fa?.calories || 0,
        steps: 0,
        intensity_level: 'moderate',
        notes: `Imported from Strava${fa?.raw_data?.name ? ` — ${fa?.raw_data?.name}` : ''}${fa?.distance ? ` | ${(fa?.distance / 1000)?.toFixed(2)} km` : ''}`,
        workout_date: activityDate,
        sync_status: 'synced',
        external_activity_id: fa?.id,
      };
    });

  if (!toInsert?.length) return { imported: 0, skipped: fitnessActivities?.length };

  // 4. Insert new logs in batches of 50
  let imported = 0;
  const batchSize = 50;
  for (let i = 0; i < toInsert?.length; i += batchSize) {
    const batch = toInsert?.slice(i, i + batchSize);
    const { error: insertError } = await supabase
      ?.from('workout_logs')
      ?.insert(batch);
    if (insertError) {
      console.error('[Strava Import] Batch insert error:', insertError);
      // Continue with remaining batches
    } else {
      imported += batch?.length;
    }
  }

  return { imported, skipped: fitnessActivities?.length - toInsert?.length };
}

/**
 * Triggers a manual activity sync for the user via the Edge Function,
 * then auto-imports the synced activities into workout_logs.
 */
export async function syncStravaActivities(userId) {
  if (!SUPABASE_URL) {
    throw new Error('Supabase URL is not configured.');
  }

  let response = await fetch(`${SUPABASE_URL}/functions/v1/fitness-activity-sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': import.meta.env?.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ user_id: userId }),
  });

  let data = await response?.json();

  if (!response?.ok) {
    throw new Error(data?.error || 'Failed to sync Strava activities');
  }

  // After syncing, auto-import new activities into workout_logs
  try {
    const importResult = await importStravaActivitiesToLogs(userId);
    console.log('[Strava Sync] Auto-import result:', importResult);
    data.importResult = importResult;
  } catch (importErr) {
    console.error('[Strava Sync] Auto-import failed (sync still succeeded):', importErr);
  }

  return data;
}
